'use client';

import { useCreatorVideos } from '@/api/queries/creators.queries';
import { useCreatorGuestSummary } from './useCreatorGuestSummary';
import { Button } from '@/components/ui/button';
import { TicketBand } from '@/features/cards/variants/TicketBand';
import { trackUnlockCtaClick } from '@/features/freemium/components/unlock-cta';
import type {
  TicketBandGlobe,
  TicketBandGuestData,
  TicketBandMemberData,
  TicketBandNewItem,
  TicketBandResume,
} from '@/features/cards/variants/TicketBand/types';
import { useAuthStore } from '@/shared/stores/auth-store';
import { getChoosePlanHrefForCreator } from '@/shared/utils/routes';
import type { Creator, Video } from '@/types';
import { ArrowRight } from 'lucide-react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';

const CreatorMiniGlobe = dynamic(
  () => import('./CreatorMiniGlobe').then((m) => m.CreatorMiniGlobe),
  { ssr: false }
);

interface CreatorTicketBandContainerProps {
  creator: Creator;
  handler: string;
  /** Guest = not authenticated or not subscribed. Picks variant + data shape. */
  isGuest: boolean;
}

const NEW_SINCE_LIMIT = 3;
const NEW_SINCE_FALLBACK_DAYS = 30;
const NEW_BADGE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const LAST_VISIT_KEY_PREFIX = 'tabootv.last-visit.';

function deriveBadge(video: Video): 'LATEST' | 'NEW' | undefined {
  if (video.latest === true) return 'LATEST';
  if (typeof video.published_at === 'string') {
    const t = new Date(video.published_at).getTime();
    if (Number.isFinite(t) && t > Date.now() - NEW_BADGE_WINDOW_MS) return 'NEW';
  }
  return undefined;
}

function formatDuration(seconds: number | undefined): string | undefined {
  if (seconds === undefined || seconds <= 0) return undefined;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function locationLabel(video: Video): string | undefined {
  const loc = video.location;
  if (loc === null || loc === undefined || loc.length === 0) return undefined;
  return loc.toUpperCase();
}

// TODO(backend): UserProgress has no `last_watched_at` / `updated_at` field.
// Current approximation picks the most-recently-STARTED unfinished video via
// `first_watched_at`. That's wrong when a user resumes an older started
// video — we'd still point them at the newer one. True resume logic needs
// either `last_watched_at` added to UserProgress, or a dedicated
// `GET /creators/{id}/resume` endpoint returning the single video the user
// should pick up next. Docs: backend-requests/*.md.
function pickResumeVideo(videos: Video[]): Video | undefined {
  const started = videos.filter((v) => {
    const up = v.user_progress;
    if (up === null || up === undefined) return false;
    if (up.completed === true) return false;
    if (typeof up.position !== 'number' || up.position <= 0) return false;
    return true;
  });
  started.sort((a, b) => {
    const ta =
      a.user_progress?.first_watched_at !== null && a.user_progress?.first_watched_at !== undefined
        ? new Date(a.user_progress.first_watched_at).getTime()
        : 0;
    const tb =
      b.user_progress?.first_watched_at !== null && b.user_progress?.first_watched_at !== undefined
        ? new Date(b.user_progress.first_watched_at).getTime()
        : 0;
    return tb - ta;
  });
  return started[0];
}

function buildResume(video: Video | undefined): TicketBandResume | undefined {
  if (video === undefined) return undefined;
  const up = video.user_progress;
  if (up === null || up === undefined) return undefined;

  const title = video.title ?? 'Continue watching';
  const href = `/videos/${video.uuid ?? video.id}`;

  const posSec = typeof up.position === 'number' ? up.position : 0;
  const percent =
    typeof up.duration === 'number' && up.duration > 0
      ? Math.round((posSec / up.duration) * 100)
      : 0;

  // TODO(backend): three gaps on the resume card eyebrow + sub line.
  // 1. Series episode label: Video has `series_id` only — no
  //    `season_number` / `episode_number`. The prototype shows
  //    "Continue · S02 E04" when the resume is a series episode;
  //    without those fields we fall back to plain "Continue".
  // 2. "paused N days ago": needs `last_watched_at` on UserProgress
  //    (see resume-picker TODO above). Same upstream fix covers this.
  // 3. ISO-prefixed locator ("AF · Kabul"): we have `video.location`
  //    ("Kabul, Afghanistan") and the country object, but we render a
  //    plain location string here. Enrichment would require running
  //    the video through the shared ISO resolver — trivial, but only
  //    worth doing once (1) and (2) land.
  return {
    label: 'Continue',
    title,
    sub: locationLabel(video) ?? '',
    href,
    ...(video.thumbnail_webp !== undefined && video.thumbnail_webp.length > 0
      ? { thumbnailSrc: video.thumbnail_webp }
      : video.thumbnail !== undefined && video.thumbnail.length > 0
        ? { thumbnailSrc: video.thumbnail }
        : {}),
    thumbnailAlt: title,
    progressPercent: percent,
  };
}

// TODO(backend): no per-user per-creator last-visit timestamp exists in the
// API. Client-side we fall back to localStorage, which is not cross-device
// and resets on clear-site-data. A proper fix needs either a last-visit
// field on the creator-profile response, or a dedicated endpoint. Until
// then we filter by `published_at > lastVisit`, with a 30-day fallback
// when there's no prior visit stored.
function buildNewItems(videos: Video[], cutoffMs: number): ReadonlyArray<TicketBandNewItem> {
  return videos
    .filter((v) => {
      if (typeof v.published_at !== 'string') return false;
      const t = new Date(v.published_at).getTime();
      return Number.isFinite(t) && t >= cutoffMs;
    })
    .slice(0, NEW_SINCE_LIMIT)
    .map((v) => {
      const loc = locationLabel(v);
      const dur = formatDuration(v.duration);
      const thumb = v.thumbnail_webp ?? v.thumbnail;
      const badge = deriveBadge(v);
      return {
        title: v.title ?? '',
        href: `/videos/${v.uuid ?? v.id}`,
        ...(loc !== undefined ? { location: loc } : {}),
        ...(dur !== undefined ? { duration: dur } : {}),
        ...(thumb !== undefined && thumb.length > 0 ? { thumbnailSrc: thumb } : {}),
        ...(badge !== undefined ? { badge } : {}),
      } satisfies TicketBandNewItem;
    });
}

function readLastVisit(creatorId: number): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${LAST_VISIT_KEY_PREFIX}${creatorId}`);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeLastVisit(creatorId: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${LAST_VISIT_KEY_PREFIX}${creatorId}`, String(Date.now()));
  } catch {
    // Storage unavailable (private mode, quota); silent no-op.
  }
}

// TODO(backend): Creator payload has `countries_recorded: number` but no
// `countries: string[]` list of ISO-2 codes. Deriving the list on FE would
// require paginating every video and deduping — expensive + incomplete.
// The right fix is a `countries: string[]` field on the creator profile.
// Until then we render the globe without the ISO list.
function buildGlobe(creator: Creator, handler: string): TicketBandGlobe {
  const countries = creator.countries_recorded ?? 0;
  const videos = creator.total_videos ?? creator.videos_count ?? 0;
  const statLabel = videos > 0 ? `Countries · ${videos} Stops` : 'Countries';
  return {
    coordStatus: '● LIVE',
    statValue: countries,
    statLabel,
    exploreLabel: 'Explore the globe',
    exploreHref: `/globe?creator=${handler}`,
  };
}

function buildGuest(
  creator: Creator,
  locked: number,
  lockedShortsCount: number,
  ctaHref: string
): TicketBandGuestData {
  const name = creator.name;
  const bullets: string[] = [];
  if (locked > 0) bullets.push(`**${locked}** of ${name}'s locked videos`);
  if (lockedShortsCount > 0) {
    bullets.push(
      locked > 0
        ? `**${lockedShortsCount} shorts** unlocked`
        : `**${lockedShortsCount} shorts** unlocked from ${name}`
    );
  }
  // Skip the platform bullet when there's nothing creator-specific — the
  // headline already carries the "every creator / every exclusive" pitch.
  if (locked > 0 || lockedShortsCount > 0) {
    bullets.push('**Every creator. Every exclusive.**');
  }

  let headline: string;
  if (locked >= 2) {
    headline = `${locked} more from ${name}.\nAnd every creator on Taboo TV.`;
  } else if (locked === 1) {
    headline = `One more from ${name}.\nAnd every creator on Taboo TV.`;
  } else {
    headline = `Every creator on Taboo TV.\nEvery exclusive. One membership.`;
  }

  return {
    eyebrow: 'Taboo TV Subscription · All-Access',
    headline,
    emphasis: 'every creator',
    subcopy:
      'One membership, every exclusive on the platform. No ads. No paywall mid-video. Cancel whenever.',
    bullets,
    ctaLabel: 'Subscribe to unlock',
    ctaHref,
    ctaMeta: 'cancel anytime',
  };
}

/**
 * Platform-side adapter that wires creator data + auth state into
 * the presentation-only `TicketBand` primitive. Guest variant pulls
 * counts from the `Creator` payload; member variant seeds mock
 * resume + new-since data from `useCreatorVideos` until a dedicated
 * BE endpoint lands.
 */
export function CreatorTicketBandContainer({
  creator,
  handler,
  isGuest,
}: CreatorTicketBandContainerProps): ReactElement | null {
  const router = useRouter();
  const authUser = useAuthStore((s) => s.user);
  // Own-page: creator viewing their own channel. Hide the ticket band entirely —
  // the member/guest copy ("Welcome back", "Subscribe to unlock") doesn't apply
  // to the creator themselves. A dedicated owner variant will replace this later.
  const isOwnPage = authUser !== null && authUser.id === creator.user_id;

  const { data: videosData } = useCreatorVideos(isGuest || isOwnPage ? undefined : creator.id, {
    sort_by: 'newest',
  });
  const guestSummary = useCreatorGuestSummary(creator.id, !!isGuest && !isOwnPage);

  const globe = useMemo(() => buildGlobe(creator, handler), [creator, handler]);

  const choosePlanHref = getChoosePlanHrefForCreator(handler);

  const handleSubscribeClick = useCallback(() => {
    trackUnlockCtaClick('ticket_band', choosePlanHref, handler);
    router.push(choosePlanHref);
  }, [router, choosePlanHref, handler]);

  const [lastVisit, setLastVisit] = useState<number | null>(null);
  useEffect(() => {
    if (isGuest) return;
    setLastVisit(readLastVisit(creator.id));
    writeLastVisit(creator.id);
  }, [isGuest, creator.id]);

  const memberData = useMemo<TicketBandMemberData | undefined>(() => {
    if (isGuest) return undefined;
    // TODO(backend): `useCreatorVideos` returns only the first page of
    // the catalog. Counts derived from it (like "how many completed") will
    // be undercounts for creators with large catalogs. We've stopped
    // surfacing those counts in the headline for that reason. When BE
    // ships a per-creator watch-stats endpoint, we can reintroduce them.
    const videos = videosData?.data ?? [];
    const resumeVideo = pickResumeVideo(videos);
    const resume = buildResume(resumeVideo);

    const cutoff = lastVisit ?? Date.now() - NEW_SINCE_FALLBACK_DAYS * 24 * 60 * 60 * 1000;
    const newItems = buildNewItems(videos, cutoff);

    const headline = `Still on the trail with ${creator.name}.`;
    const sub =
      newItems.length > 0
        ? `${newItems.length} new since you last checked.`
        : 'Pick up where you left off.';

    return {
      greeting: 'Welcome back',
      headline,
      sub,
      ...(resume !== undefined ? { resume } : {}),
      ...(newItems.length > 0
        ? { newSince: { items: newItems, seeAllHref: `/${handler}/videos` } }
        : {}),
    };
  }, [isGuest, videosData?.data, creator, handler, lastVisit]);

  const globeSlot = <CreatorMiniGlobe />;

  // Own-page placeholder: extracted CTA only, pending a dedicated owner variant.
  if (isOwnPage) {
    return (
      <div className="flex justify-center">
        <Button asChild variant="outline" size="lg">
          <Link href={`/globe?creator=${handler}`}>
            Explore your journey
            <ArrowRight className="w-4 h-4" />
          </Link>
        </Button>
      </div>
    );
  }

  if (isGuest) {
    const guest = buildGuest(
      creator,
      guestSummary.totalLocked,
      guestSummary.totalLockedShorts,
      choosePlanHref
    );
    return (
      <TicketBand
        variant="guest"
        globe={globe}
        guest={guest}
        onSubscribeClick={handleSubscribeClick}
        globeSlot={globeSlot}
      />
    );
  }
  if (memberData === undefined) return null;
  return <TicketBand variant="member" globe={globe} member={memberData} globeSlot={globeSlot} />;
}
